import { lazy, Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SiteFooter, SiteHeader } from '@/features/site/components/SiteChrome';
import { useSite } from '@/features/site/hooks/useSite';
import { StorefrontShellSkeleton } from '@/features/storefront/components/StoreSkeletons';
import { MobileBottomNav } from '@/ui/MobileBottomNav';
import '@/features/site/site.css';

// Its own chunk: a customer's shop on a subdomain never downloads it.
const StoreShell = lazy(() =>
  import('@/features/store/components/StoreShell').then((m) => ({ default: m.StoreShell })),
);

/**
 * The shop's chrome: its own header and footer around every public page.
 *
 * Which shop is decided by the subdomain (acme.example.com), so this layout
 * fetches the shop's website first and dresses every page in it. Every shop
 * shares the platform's palette on a white page; what differs is the name,
 * logo and content.
 *
 * At the platform's own address - no shop subdomain, so `localhost:5173` or
 * the bare production domain - the API says `is_platform_home`, and every
 * page is dressed in the platform store instead (see features/store).
 */

function SiteStatus({ title, message }) {
  return (
    <main className="site-status">
      <div>
        <h1>{title}</h1>
        <p>{message}</p>
      </div>
    </main>
  );
}

export function StorefrontLayout() {
  const { data: site, error, isLoading, refetch } = useSite();
  const location = useLocation();
  const isHome = location.pathname === '/';
  // Pages that pin an action bar above the tab bar on phones need room for it
  // under their content, or it covers the end of the page and the footer.
  const hasActionBar = /^\/(products\/|cart|checkout|orders\/)/.test(location.pathname);

  useEffect(() => {
    if (site?.name && !site.is_platform_home) document.title = site.name;
  }, [site?.name, site?.is_platform_home]);

  if (isLoading) return <StorefrontShellSkeleton />;

  if (error?.code === 'storefront_not_found') {
    return (
      <SiteStatus
        title="Store not found"
        message="There is no store at this address. Check the link and try again."
      />
    );
  }
  if (error?.code === 'storefront_unavailable') {
    return (
      <SiteStatus title="This store is currently unavailable" message="Please check back later." />
    );
  }
  if (!site) {
    return (
      <main className="site-status">
        <div>
          <h1>Could not load the store</h1>
          <p>{error?.message ?? 'Something went wrong.'}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-4 rounded-full bg-ink px-5 py-2 text-sm font-semibold text-white"
          >
            Try again
          </button>
        </div>
      </main>
    );
  }

  if (site.is_platform_home) {
    return (
      <Suspense fallback={<StorefrontShellSkeleton />}>
        <StoreShell />
      </Suspense>
    );
  }

  return (
    <div
      className={`site flex min-h-screen flex-col bg-white md:pb-0 ${hasActionBar ? 'pb-[8.5rem]' : 'pb-16'}`}
    >
      <SiteHeader site={site} />
      {/* The landing page is full-bleed; every other page sits in a column.
          At least a screen tall, so the footer stays out of sight while a
          page is still loading rather than riding up under its skeleton. */}
      <main className={isHome ? 'min-h-svh flex-1' : 'site-page min-h-svh w-full flex-1'}>
        <Outlet />
      </main>
      <SiteFooter site={site} />
      <MobileBottomNav />
    </div>
  );
}
